import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Row, Col, Badge, Spinner, Button } from 'reactstrap';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { useSnackbar } from 'notistack';
import { FaRobot, FaDollarSign, FaNewspaper, FaCheck, FaTimes } from 'react-icons/fa';

const AgentStats = () => {
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    fetchStats();

    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);

  }, []);

  const fetchStats = async () => {
    try {
      const response = await axiosPrivate.get('/madiscovery/stats');
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      setLoading(false);
    }
  };

  const handleRunAgent = async () => {
    setRunning(true);
    try {
      const response = await axiosPrivate.post('/madiscovery/run');
      const result = response.data;
      const message = result.skippedDueToLearning > 0
        ? `Discovery completed! Articles: ${result.articlesFound}, Extractions: ${result.extractionsSuccessful}, Pending: ${result.approvalsCreated}, Skipped (learning): ${result.skippedDueToLearning}, Time: ${result.executionTimeMs}ms`
        : `Discovery completed! Articles: ${result.articlesFound}, Extractions: ${result.extractionsSuccessful}, Pending: ${result.approvalsCreated}, Time: ${result.executionTimeMs}ms`;

      enqueueSnackbar(message, { variant: 'success' });
      await fetchStats();
    } catch (error) {
      enqueueSnackbar('Discovery failed: ' + error.message, { variant: 'error' });
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner color="primary" />
        <p className="mt-2">Loading agent statistics...</p>
      </div>
    );
  }

  return (
    <div className="agent-stats">
      <Row className="mb-3">
        <Col>
          <h2>
            <FaRobot className="me-2" />
            AI M&A Discovery Agent - Dashboard
          </h2>
          <p className="text-muted">
            Autonomous agent for discovering M&A transactions in real-time
          </p>
        </Col>
        <Col xs="auto">
          <Button
            color="primary"
            size="lg"
            onClick={handleRunAgent}
            disabled={running}
          >
            {running ? (
              <>
                <Spinner size="sm" className="me-2" />
                Running...
              </>
            ) : (
              <>
                <FaRobot className="me-2" />
                Run Agent Now
              </>
            )}
          </Button>
        </Col>
      </Row>

      <Row>
        {}
        <Col md="6" lg="3" className="mb-4">
          <Card className="border-primary">
            <CardHeader className="bg-primary text-white">
              <strong>Today</strong>
            </CardHeader>
            <CardBody>
              <div className="stat-item">
                <FaRobot size={24} className="text-primary mb-2" />
                <h3>{stats?.today?.runs || 0}</h3>
                <p className="text-muted mb-0">Agent Runs</p>
              </div>
              <hr />
              <div className="stat-item">
                <FaNewspaper size={20} className="text-info mb-2" />
                <h4>{stats?.today?.articlesFound || 0}</h4>
                <p className="text-muted mb-0">Articles Found</p>
              </div>
              <hr />
              <div className="stat-item">
                <FaDollarSign size={20} className="text-success mb-2" />
                <h4>${(stats?.today?.cost || 0).toFixed(4)}</h4>
                <p className="text-muted mb-0">Cost Today</p>
              </div>
            </CardBody>
          </Card>
        </Col>

        {}
        <Col md="6" lg="3" className="mb-4">
          <Card className="border-success">
            <CardHeader className="bg-success text-white">
              <strong>This Month</strong>
            </CardHeader>
            <CardBody>
              <div className="stat-item">
                <FaRobot size={24} className="text-success mb-2" />
                <h3>{stats?.thisMonth?.runs || 0}</h3>
                <p className="text-muted mb-0">Total Runs</p>
              </div>
              <hr />
              <div className="stat-item">
                <FaDollarSign size={20} className="text-warning mb-2" />
                <h4>${(stats?.thisMonth?.totalCost || 0).toFixed(4)}</h4>
                <p className="text-muted mb-0">Total Cost</p>
                <small className="text-success">
                  Budget: {((stats?.thisMonth?.totalCost || 0) / 10 * 100).toFixed(1)}% of $10
                </small>
              </div>
            </CardBody>
          </Card>
        </Col>

        {}
        <Col md="6" lg="3" className="mb-4">
          <Card className="border-warning">
            <CardHeader className="bg-warning text-dark">
              <strong>Pending Review</strong>
            </CardHeader>
            <CardBody>
              <div className="stat-item">
                <FaNewspaper size={24} className="text-warning mb-2" />
                <h2>{stats?.thisMonth?.pendingApprovals || 0}</h2>
                <p className="text-muted mb-0">Awaiting Approval</p>
              </div>
              {(stats?.thisMonth?.pendingApprovals || 0) > 0 && (
                <div className="mt-3">
                  <Badge color="warning" pill className="w-100 p-2">
                    Action Required
                  </Badge>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        {}
        <Col md="6" lg="3" className="mb-4">
          <Card className="border-info">
            <CardHeader className="bg-info text-white">
              <strong>Decisions</strong>
            </CardHeader>
            <CardBody>
              <div className="stat-item">
                <FaCheck size={20} className="text-success mb-2" />
                <h4>{stats?.thisMonth?.approved || 0}</h4>
                <p className="text-muted mb-0">Approved</p>
              </div>
              <hr />
              <div className="stat-item">
                <FaTimes size={20} className="text-danger mb-2" />
                <h4>{stats?.thisMonth?.rejected || 0}</h4>
                <p className="text-muted mb-0">Rejected</p>
              </div>
              <hr />
              <div className="text-center">
                <small className="text-muted">
                  Approval Rate:{' '}
                  {stats?.thisMonth?.approved + stats?.thisMonth?.rejected > 0
                    ? ((stats?.thisMonth?.approved / (stats?.thisMonth?.approved + stats?.thisMonth?.rejected)) * 100).toFixed(1)
                    : 0}%
                </small>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {}
      <Row className="mt-3">
        <Col>
          <Card>
            <CardHeader>
              <strong>📊 Cost Analysis</strong>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md="4">
                  <div className="text-center">
                    <h5>OpenAI GPT-4o-mini</h5>
                    <h3 className="text-primary">${(stats?.thisMonth?.totalCost || 0).toFixed(4)}</h3>
                    <p className="text-muted mb-0">~{((stats?.thisMonth?.totalCost || 0) * 0.9).toFixed(3)} EUR/month</p>
                  </div>
                </Col>
                <Col md="4">
                  <div className="text-center">
                    <h5>RSS Feeds</h5>
                    <h3 className="text-success">FREE</h3>
                    <p className="text-muted mb-0">Unlimited queries</p>
                  </div>
                </Col>
                <Col md="4">
                  <div className="text-center">
                    <h5>Google Custom Search</h5>
                    <h3 className="text-success">FREE</h3>
                    <p className="text-muted mb-0">Under 100/day limit</p>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>{`
        .stat-item {
          text-align: center;
        }
        .stat-item h2, .stat-item h3, .stat-item h4 {
          margin-bottom: 0.5rem;
          font-weight: bold;
        }
      `}</style>
    </div>
  );
};

export default AgentStats;
